/**
 * Layout shell for the Knowledge Processing Console.
 *
 * Left/centre: the document header, the processing pipeline strip and the
 * centre visual (2D orbit or three.js scene) with the validation feed beside it.
 * Right: live yield and the seven-layer validation panel.
 *
 * Colours come from the app's own theme tokens (bg-card, border-border,
 * text-foreground, ...) and the same indigo/emerald/amber/red status palette
 * ValidationResultPanel uses, so the console reads as part of the dashboard in
 * both light and dark mode.
 */
import React from "react";
import { X, FileText, Ban, Minus, AlertOctagon, ArrowLeft } from "lucide-react";
import type { ValidateFileResult } from "../../../api/frameworksApi";
import {
  PIPELINE_STAGES,
  VALIDATION_LAYERS,
  YIELD_TONE_CLASS,
  buildYield,
  buildStageStates,
  type ConsolePhase,
  type ConsoleStageState,
  type PipelineRun,
} from "./processingPipeline";
import {
  ExtractionFeed,
  Panel,
  ConsoleLogo,
  ConsoleKeyframes,
} from "./ConsoleParts";

export interface ConsoleShellProps {
  /** Name of the file being processed. */
  fileName?: string;
  /** Human readable file size, e.g. "11.5 MB". */
  fileSize?: string;
  /** Document title as entered by the user. */
  documentTitle?: string;
  /** Document type label, e.g. "Policy". */
  documentType?: string;
  phase: ConsolePhase;
  run: PipelineRun;
  result?: ValidateFileResult | null;
  errorMessage?: string | null;
  /** Centre visual - the orbit canvas or the three.js scene. */
  core: React.ReactNode;
  /** Shown next to the brand, e.g. "3D scene". */
  variantLabel?: string;
  onCancel?: () => void;
  /**
   * Optional back-to-picker action. When present, a Back button is shown in
   * the top bar so the user can swap the current file for a different one
   * without cancelling the whole flow.
   */
  onBack?: () => void;
  /** Extra controls for the top bar (used by the standalone routes). */
  topBarExtra?: React.ReactNode;
  /**
   * Blocking message shown in the top bar, e.g. "Cannot upload - validation
   * errors found". The console is the results surface, so this is where the
   * user learns the upload is blocked.
   */
  notice?: { title: string; detail?: string } | null;
  /** Action bar pinned to the bottom of the console (Cancel / Save / ...). */
  footer?: React.ReactNode;
  /**
   * Sends the console to the floating status bubble while the job keeps
   * running in the background.
   */
  onMinimize?: () => void;
  /**
   * Render as a normal in-flow panel instead of a full-screen overlay, so the
   * app sidebar and top nav stay visible. Used by the routed console pages;
   * the validation flow keeps the overlay.
   */
  inline?: boolean;
}

/** Status dot/text palette, matching ValidationResultPanel's colours. */
const STATUS_TONE = {
  running: {
    dot: "bg-indigo-500",
    text: "text-indigo-600 dark:text-indigo-400",
  },
  pass: {
    dot: "bg-emerald-500",
    text: "text-emerald-600 dark:text-emerald-400",
  },
  warn: {
    dot: "bg-amber-500",
    text: "text-amber-600 dark:text-amber-400",
  },
  fail: {
    dot: "bg-red-500",
    text: "text-red-600 dark:text-red-400",
  },
} as const;

type StageState = ConsoleStageState;

export function ConsoleShell({
  fileName,
  fileSize,
  documentTitle,
  documentType,
  phase,
  run,
  result = null,
  errorMessage = null,
  core,
  variantLabel,
  onCancel,
  onBack,
  topBarExtra,
  notice = null,
  footer,
  onMinimize,
  inline = false,
}: ConsoleShellProps) {
  const stages = PIPELINE_STAGES;
  const active = stages[Math.min(run.stage, stages.length - 1)];
  const yields = buildYield(result, phase);

  /* The backend's own stage report wins once it lands; until then the local
     run animation drives the strip. */
  const apiStageStates = buildStageStates(result);
  const stageState = (i: number): StageState =>
    apiStageStates?.[i] ??
    (run.done || i < run.stage ? "done" : i === run.stage ? "active" : "pending");

  /* When the pipeline halts on a failed stage, pop a transient callout above
     that step so the user's eye lands where the flow actually stopped - the
     small red icon and the top-bar notice on their own are easy to miss. */
  const failedStageIndex = React.useMemo(() => {
    if (phase !== "error" && !apiStageStates) return -1;
    if (apiStageStates) {
      const idx = apiStageStates.findIndex((s) => s === "failed");
      if (idx >= 0) return idx;
    }
    if (phase === "error") return Math.min(run.stage, stages.length - 1);
    return -1;
  }, [apiStageStates, phase, run.stage, stages.length]);

  const [failurePop, setFailurePop] = React.useState<
    { index: number; label: string; visible: boolean } | null
  >(null);
  const lastFailureRef = React.useRef<number>(-1);
  React.useEffect(() => {
    if (failedStageIndex < 0) {
      lastFailureRef.current = -1;
      setFailurePop(null);
      return;
    }
    // Same failed step as last render - don't retrigger the animation.
    if (lastFailureRef.current === failedStageIndex) return;
    lastFailureRef.current = failedStageIndex;
    const stage = stages[failedStageIndex];
    setFailurePop({ index: failedStageIndex, label: stage.label, visible: true });
    const hideAt = window.setTimeout(
      () => setFailurePop((p) => (p ? { ...p, visible: false } : null)),
      3500,
    );
    const removeAt = window.setTimeout(() => setFailurePop(null), 3900);
    return () => {
      window.clearTimeout(hideAt);
      window.clearTimeout(removeAt);
    };
  }, [failedStageIndex, stages]);

  /* ── Seven-layer validation ──
     The seven layers themselves are a fixed part of the knowledge model, so the
     panel always lists all of them. Only their outcome comes from the backend
     (ValidateFileResult.sevenLayerValidation), and it is binary: cleared or not
     cleared. No scores are shown - a layer that did not run reads "Not cleared"
     in red rather than borrowing a percentage from anywhere. */
  const sevenLayer = result?.sevenLayerValidation ?? null;
  const apiLayers = (sevenLayer?.layers ?? []) as any[];
  const clearedCount = sevenLayer?.clearedLayers ?? 0;
  /* Until the response is in, a layer is simply not decided yet - it must not
     read as failed while the job is still processing. When the request itself
     errors out (e.g. server 504), we still don't have per-layer outcomes but
     we do know they will never arrive, so a third `layersErrored` state
     switches the pills to "Not run" instead of a misleading "Pending". */
  const layersSettled = !!result;
  const layersErrored = phase === "error" && !result;

  const layerRows = VALIDATION_LAYERS.map((def, i) => {
    // Look for a matching entry by key first (name match), then by
    // ordinal position as a fallback for older payloads.
    const entryByKey = apiLayers.find((l: any) => l?.key === def.key);
    const entry = entryByKey ?? apiLayers[i];

    /* A layer counts as "evaluated" only when we actually got a payload
       row for it. This distinguishes three states in the panel:
         - evaluated + cleared         → "Cleared"    (green)
         - evaluated + !cleared        → "Not cleared" (red)
         - !evaluated (no data)        → "Not evaluated" (neutral)
       The old code showed unscored layers as "Not cleared" in red,
       which read as failure even when the layer simply wasn't run by
       the semantic API (e.g. byEvidence only carried 3 of 7 layers). */
    const evaluated = !!entry;
    const cleared = evaluated
      ? (entry.passed ??
        String(entry.status ?? "").toLowerCase() === "cleared")
      : i < clearedCount;

    return {
      key: def.key,
      // Always use the local Title Case label - the API sometimes returns
      // uppercase keys (e.g. "DOMAIN"), which we don't want to surface.
      label: def.label,
      icon: def.icon,
      cleared: evaluated && cleared,
      evaluated,
    };
  });

 
  const layersTotal = VALIDATION_LAYERS.length;
  const layersCleared = layerRows.filter((r) => r.cleared).length;
  const layersNotEvaluated = layerRows.filter(
    (r) => layersSettled && !r.evaluated,
  ).length;

  /* The job's own status decides the badge when the backend sends one, so a
     PARTIAL or FAILED run never reads as "All rules passed". */
  const jobStatus = String(result?.processingJob?.status ?? "").toUpperCase();
  const jobFailed = jobStatus === "FAILED" || jobStatus === "ERROR";
  const jobPartial = jobStatus === "PARTIAL";
  const allPassed =
    !!result && result.summary.fail === 0 && !jobFailed && !jobPartial;

  const statusKey: keyof typeof STATUS_TONE =
    phase === "error" || jobFailed
      ? "fail"
      : run.done
        ? allPassed
          ? "pass"
          : "warn"
        : "running";

  const statusLabel =
    phase === "error" || jobFailed
      ? "Validation failed"
      : run.done
        ? allPassed
          ? "All rules passed"
          : "Needs review"
        : active.label;

  const tone = STATUS_TONE[statusKey];

  const body = (
    <div
      className={`flex min-h-0 flex-col text-foreground ${
        inline
          ? "relative min-h-[640px] w-full min-w-0 flex-1 overflow-hidden rounded-2xl border border-border bg-background"
          : // No max-h clamp: the body grows to its natural height (orbit
            // + feed + right panels), and the outer overlay (see the return
            // at the bottom of this file) handles vertical scroll when the
            // viewport can't fit it. `min-h-[620px]` gives internal
            // `flex-1` regions (Panel > core+feed grid) a bounded parent
            // to allocate against so the orbit doesn't collapse to zero
            // when the body's own height is otherwise unconstrained.
            "relative min-h-[620px] w-full max-w-[1400px] overflow-hidden rounded-2xl border border-border bg-background shadow-2xl"
      }`}
      role={inline ? "region" : "dialog"}
      aria-modal={inline ? undefined : true}
      aria-label="Knowledge processing console"
    >
      <ConsoleKeyframes />

      {/* ── Top bar ── */}
      <header className="relative z-10 flex flex-wrap items-center justify-between gap-3 border-b border-border bg-card px-4 py-3 sm:px-6">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <ConsoleLogo />
          <div className="min-w-0">
            {/* Keep the brand line intact - the flex-wrap on the parent
                header already drops the right-hand group to a new line when
                the viewport is too narrow, which is a much cleaner break
                than splitting the title as "Knowledge Processing" / "Console". */}
            <div className="truncate text-sm font-bold leading-tight whitespace-nowrap">
              Knowledge Processing Console
            </div>
            <div className="truncate text-[11px] tracking-wide text-muted-foreground whitespace-nowrap">
              Compliance Knowledge Hub · Content Registry
              {variantLabel ? ` · ${variantLabel}` : ""}
            </div>
          </div>
        </div>

        <div className="flex min-w-0 flex-wrap items-center gap-3 sm:gap-4">
          <div className={`flex items-center gap-2 text-xs font-medium ${tone.text}`}>
            <span
              className={`size-2 flex-none rounded-full ${tone.dot}`}
              style={{
                animation: run.done
                  ? "none"
                  : "rgc-blink 1.6s ease-in-out infinite",
              }}
            />
            {statusLabel}
          </div>
          {notice && (
            // Cap the notice card so its long detail text doesn't crowd out
            // the brand line on the left. The card still wraps its own text.
            <div className="flex min-w-0 max-w-sm items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 dark:border-red-500/20 dark:bg-red-500/10">
              <Ban className="mt-0.5 size-3.5 flex-none text-red-600 dark:text-red-400" />
              <div className="min-w-0">
                <div className="break-words text-xs font-bold text-red-700 dark:text-red-400">
                  {notice.title}
                </div>
                {notice.detail && (
                  <div className="break-words text-[11px] leading-snug text-red-700/90 dark:text-red-400/90">
                    {notice.detail}
                  </div>
                )}
              </div>
            </div>
          )}
          {topBarExtra}
          {onMinimize && (
            <button
              type="button"
              onClick={onMinimize}
              title="Keep running in the background"
              className="flex items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-[#475569] transition-colors hover:bg-[#f1f5f9] dark:text-white/60 dark:hover:bg-white/[0.04]"
            >
              <Minus className="size-3.5" />
              Minimize
            </button>
          )}
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              title="Back to file picker"
              aria-label="Back to file picker"
              className="flex items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-[#475569] transition-colors hover:bg-[#f1f5f9] dark:text-white/60 dark:hover:bg-white/[0.04]"
            >
              <ArrowLeft className="size-3.5" />
              Back
            </button>
          )}
          {/* The action bar already carries a labelled Cancel, so the top bar
              keeps only a compact dismiss affordance - two "Cancel" buttons on
              one screen read as two different actions. Consoles rendered
              without a footer keep the labelled button. */}
          {onCancel &&
            (footer ? (
              <button
                type="button"
                onClick={onCancel}
                title={run.done ? "Close" : "Cancel"}
                aria-label={run.done ? "Close" : "Cancel"}
                className="grid size-8 flex-none place-items-center rounded-lg border border-border text-[#475569] transition-colors hover:bg-[#f1f5f9] dark:text-white/60 dark:hover:bg-white/[0.04]"
              >
                <X className="size-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={onCancel}
                className="flex items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-[#475569] transition-colors hover:bg-[#f1f5f9] dark:text-white/60 dark:hover:bg-white/[0.04]"
              >
                <X className="size-3.5" />
                {run.done ? "Close" : "Cancel"}
              </button>
            ))}
        </div>
      </header>

      {/* ── Main grid ──
          `flex-1` claims the space the body's min-h leaves after header +
          footer, giving the orbit a bounded parent to render into. When
          content overflows that allocation the grid grows and the outer
          overlay scrolls the whole modal - a single scrollbar rather than
          nested internal ones. */}
      <div className="relative z-10 grid min-h-0 flex-1 gap-4 p-4 sm:p-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,320px)] xl:grid-cols-[minmax(0,1fr)_minmax(0,360px)]">
        {/* CENTER: document header + pipeline strip + core + feed */}
        <div className="flex min-h-0 flex-col">
          <Panel grow noHeader>
            {/* Document header */}
            <div className="flex flex-wrap items-center gap-3 px-4 pb-2.5 pt-2.5">
              <span className="grid size-10 flex-none place-items-center rounded-lg border border-border bg-[#f8fafc] text-indigo-500 dark:bg-white/[0.03] dark:text-indigo-400">
                <FileText className="size-5" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="break-words text-sm font-bold leading-tight">
                  {documentTitle ||
                    result?.document?.title ||
                    fileName ||
                    result?.filename ||
                    "Untitled document"}
                </div>
                <div className="mt-0.5 break-words text-xs font-medium text-muted-foreground">
                  {[
                    documentType || result?.document?.documentType || result?.docType,
                    fileName || result?.document?.fileName || result?.filename,
                    fileSize || result?.document?.formattedFileSize,
                    result?.processingJob?.formattedDuration,
                  ]
                    .filter(Boolean)
                    .join(" · ") || "No file attached"}
                </div>
              </div>
            </div>
            <div className="h-px bg-border" />

            {/* Pipeline strip.
                Uses overflow-visible so the transient "Failed at ..." callout
                that pops below a halted stage can extend into the core area
                without being clipped. `overflow-x-auto` would cascade to
                overflow-y: auto in modern browsers and clip the popup. */}
            <div className="relative flex items-start gap-0 overflow-visible px-4 pb-2.5 pt-3">
              {stages.map((s, i) => {
                const state = stageState(i);
                const Icon = s.icon;
                return (
                  <React.Fragment key={s.key}>
                    <div className="relative flex w-[84px] flex-none flex-col items-center gap-1.5">
                      {failurePop && failurePop.index === i && (
                        <div
                          role="status"
                          aria-live="polite"
                          className="pointer-events-none absolute left-1/2 top-full z-30 mt-3 whitespace-nowrap"
                          style={{
                            animation: failurePop.visible
                              ? "rgc-fail-pop .32s cubic-bezier(.2,.9,.25,1.2) both"
                              : "rgc-fail-out .35s ease-in forwards",
                          }}
                        >
                          {/* Tail pointing up at the icon */}
                          <span
                            className="absolute -top-1 left-1/2 size-2 -translate-x-1/2 rotate-45 rounded-[2px] border-l border-t border-red-300 bg-red-500 dark:border-red-500/50"
                          />
                          <div className="flex items-center gap-1.5 rounded-lg border border-red-300 bg-red-500 px-2.5 py-1 text-[11px] font-bold text-white shadow-lg shadow-red-500/30 dark:border-red-500/50">
                            <AlertOctagon className="size-3.5 flex-none" />
                            Failed at {failurePop.label}
                          </div>
                        </div>
                      )}
                      <div
                        className={`relative grid size-10 place-items-center rounded-xl border transition-all duration-500 ${
                          state === "active"
                            ? "border-indigo-300 bg-indigo-50 text-indigo-600 dark:border-indigo-500/40 dark:bg-indigo-500/10 dark:text-indigo-400"
                            : state === "done"
                              ? "border-emerald-200 bg-emerald-50 text-emerald-600 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-400"
                              : state === "failed"
                                ? "border-red-300 bg-red-50 text-red-600 shadow-[0_0_0_3px_rgba(239,68,68,0.12)] dark:border-red-500/50 dark:bg-red-500/10 dark:text-red-400"
                                : "border-border bg-[#f8fafc] text-[#94a3b8] dark:bg-white/[0.02] dark:text-white/30"
                        }`}
                        style={{
                          transform: state === "active" ? "scale(1.08)" : "none",
                          animation:
                            state === "failed"
                              ? "rgc-fail-shake 3.6s ease-in-out infinite"
                              : undefined,
                        }}
                      >
                        <Icon className="size-[19px]" />
                        {state === "active" && (
                          <span
                            className="pointer-events-none absolute -inset-1 rounded-[14px] border-[1.5px] border-indigo-400/70"
                            style={{ animation: "rgc-ring 1.5s ease-out infinite" }}
                          />
                        )}
                        {state === "failed" && (
                          <>
                            {/* Pulsing red aura - reuses the active-state ring
                                animation so the "attention" beat is consistent,
                                just tinted red for the halted step. */}
                            <span
                              className="pointer-events-none absolute -inset-1 rounded-[14px] border-[1.5px] border-red-400/70"
                              style={{ animation: "rgc-ring 1.8s ease-out infinite" }}
                            />
                            <span
                              className="pointer-events-none absolute -inset-1 rounded-[14px] border-[1.5px] border-red-400/50"
                              style={{
                                animation: "rgc-ring 1.8s ease-out infinite",
                                animationDelay: ".9s",
                              }}
                            />
                          </>
                        )}
                      </div>
                      <div
                        className={`text-center text-[10px] leading-tight ${
                          state === "pending"
                            ? "text-muted-foreground"
                            : "text-foreground"
                        }`}
                      >
                        {s.label}
                      </div>
                    </div>
                    {i < stages.length - 1 &&
                      (state === "failed" ? (
                        /* Severed connector: two short segments with a
                           jittery red spark in the gap, so the pipeline
                           visibly breaks off after the halted stage instead
                           of reading as a completed link. */
                        <div className="relative mt-5 flex h-0.5 min-w-[10px] flex-1 items-center gap-1">
                          <span className="h-0.5 flex-1 rounded bg-red-500" />
                          <span
                            className="size-1.5 flex-none rounded-full bg-red-500 shadow-[0_0_6px_rgba(239,68,68,0.85)]"
                            style={{
                              animation:
                                "rgc-broken-jitter 1.4s ease-in-out infinite",
                            }}
                          />
                          <span className="h-0 flex-1 rounded border-t border-dashed border-red-300 dark:border-red-500/50" />
                        </div>
                      ) : (
                        <div className="relative mt-5 h-0.5 min-w-[10px] flex-1 rounded bg-[#e2e8f0] dark:bg-white/[0.08]">
                          <span
                            className="absolute inset-y-0 left-0 rounded bg-emerald-500 transition-[width] duration-500"
                            style={{
                              width: apiStageStates
                                ? state === "done"
                                  ? "100%"
                                  : "0%"
                                : run.done || i < run.stage
                                  ? "100%"
                                  : "0%",
                            }}
                          />
                        </div>
                      ))}
                  </React.Fragment>
                );
              })}
            </div>
            <div className="h-px bg-border" />

            {/* Core + feed */}
            <div className="grid min-h-0 flex-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,280px)]">
              {/* Orbit sizes itself to the container. Floor bumped to 400px
                  so the seven-layer ring (300px diameter + icon halos +
                  labels) always fits fully in view; the grid above scrolls
                  when the console runs out of height, so this floor is
                  safe. */}
              <div className="relative grid min-h-[340px] place-items-center overflow-hidden lg:min-h-[340px]">
                {core}
              </div>
              {/* Cap the feed's height on desktop so its list of rule
                  cards scrolls internally instead of stretching the whole
                  modal downward. Without this the feed grows to natural
                  height, and the right-hand seven-layer panel (which is
                  short) ends up with a big empty band beneath it. On
                  mobile the columns stack, so the cap only applies at
                  lg+ where the side-by-side layout matters. */}
              <div className="flex min-h-0 flex-col border-t border-border lg:max-h-[420px] lg:border-l lg:border-t-0">
                <div className="flex items-center gap-2 px-4 pb-2 pt-3 text-[11px] tracking-wide text-muted-foreground">
                  <span className="size-1.5 rounded-full bg-indigo-500" />
                  Validation Feed
                </div>
                <ExtractionFeed
                  result={result}
                  run={run}
                  phase={phase}
                  errorMessage={errorMessage}
                />
              </div>
            </div>
          </Panel>
        </div>

        {/* RIGHT: telemetry */}
        <div className="flex min-h-0 flex-col gap-3">
          <Panel title="Validation Summary">
            {/* All four tiles share the same type scale so no single figure
                (e.g. Compliance's "%") looks disproportionately large next
                to the two- and three-digit counts. */}
            <div className="grid grid-cols-2 gap-2 p-2.5">
              {yields.map((k) => {
                // Compliance hint looks like "55 of 72 applicable rules
                // passed" - highlight the passed count (the leading number)
                // so the eye lands on it before reading the rest.
                const isCompliance = k.label === "Compliance";
                const passedMatch = isCompliance
                  ? k.hint.match(/^(\d[\d,]*)(\s+of\s+.*)$/)
                  : null;
                return (
                  <div
                    key={k.label}
                    className="flex min-w-0 flex-col rounded-lg border border-border bg-[#f8fafc] p-2.5 dark:bg-white/[0.02]"
                  >
                    <div className="mb-1 break-words text-[10.5px] font-medium leading-tight tracking-wide text-muted-foreground">
                      {k.label}
                    </div>
                    <div
                      className={`break-words text-2xl font-bold leading-none tracking-tight xl:text-3xl ${YIELD_TONE_CLASS[k.tone]}`}
                    >
                      {k.value}
                    </div>
                    {k.hint &&
                      (passedMatch ? (
                        <div className="mt-1 break-words text-[11px] leading-snug text-muted-foreground">
                          <span
                            className={`font-bold ${YIELD_TONE_CLASS[k.tone]}`}
                          >
                            {passedMatch[1]}
                          </span>
                          {passedMatch[2]}
                        </div>
                      ) : (
                        <div className="mt-1 break-words text-[10px] leading-snug text-muted-foreground">
                          {k.hint}
                        </div>
                      ))}
                  </div>
                );
              })}
            </div>
          </Panel>

          <Panel
            title="Seven-Layer Validation"
            meta={`${layersCleared} / ${layersTotal}`}
            grow
          >
            {layersNotEvaluated > 0 && (
              /* Frame this as document feedback, not a system limitation:
                 the layers weren't scored because the document didn't
                 contain enough relevant content for them, not because the
                 API refused to run. Nudges the user to review their
                 document rather than assume something broke. */
              <div className="mx-4 mt-2 flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50/70 px-2 py-1.5 text-[10px] leading-snug text-amber-800 dark:border-amber-500/25 dark:bg-amber-500/[0.06] dark:text-amber-300">
                <span className="mt-0.5 size-1 flex-none rounded-full bg-amber-500" />
                <span>
                  {layersNotEvaluated} of {layersTotal} layer
                  {layersTotal === 1 ? "" : "s"} couldn&apos;t be assessed —
                  your document didn&apos;t contain enough relevant content.
                  Please review the document and add the missing information.
                </span>
              </div>
            )}
            <div className="flex flex-col gap-1.5 px-4 pb-3 pt-2.5 lg:min-h-0 lg:flex-1 lg:overflow-y-auto">
              {layerRows.map((layer) => {
                const cleared = layer.cleared;
                const Icon = layer.icon;
                const evaluated = layer.evaluated;
                /* Chip / pill tone maps to one of five states:
                   - errored             (muted red, "won't happen")
                   - pending             (neutral grey, request in flight)
                   - settled + cleared   (emerald - layer passed)
                   - settled + failed    (red - layer evaluated and failed)
                   - settled + !evaluated (slate - not scored by the semantic
                     API; distinct from "failed" so users don't read a
                     missing signal as a failure). */
                const notEvaluated = layersSettled && !evaluated;
                const chipTone = layersErrored
                  ? "border-red-200/70 bg-red-50/60 text-red-500 dark:border-red-500/25 dark:bg-red-500/[0.06] dark:text-red-400/70"
                  : !layersSettled
                    ? "border-border bg-[#f8fafc] text-[#94a3b8] dark:bg-white/[0.02] dark:text-white/30"
                    : notEvaluated
                      ? "border-slate-200 bg-slate-50 text-slate-500 dark:border-white/[0.08] dark:bg-white/[0.03] dark:text-white/40"
                      : cleared
                        ? "border-emerald-200 bg-emerald-50 text-emerald-600 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-400"
                        : "border-red-200 bg-red-50 text-red-600 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-400";
                const pillTone = layersErrored
                  ? "border-red-200/70 text-red-500/80 dark:border-red-500/25 dark:text-red-400/70"
                  : !layersSettled
                    ? "border-border text-muted-foreground"
                    : notEvaluated
                      ? "border-slate-200 text-slate-500 dark:border-white/[0.08] dark:text-white/50"
                      : cleared
                        ? "border-emerald-200 text-emerald-600 dark:border-emerald-500/30 dark:text-emerald-400"
                        : "border-red-200 text-red-600 dark:border-red-500/30 dark:text-red-400";
                const pillLabel = layersErrored
                  ? "Not run"
                  : !layersSettled
                    ? "Pending"
                    : notEvaluated
                      ? "Not evaluated"
                      : cleared
                        ? "Cleared"
                        : "Not cleared";
                const rowTitle = notEvaluated
                  ? "Your document didn't contain enough relevant content for this layer to be assessed. Please review the document."
                  : undefined;

                return (
                  <div
                    key={layer.key}
                    className="flex items-center gap-2.5"
                    title={rowTitle}
                  >
                    <span
                      className={`grid size-7 flex-none place-items-center rounded-lg border transition-colors ${chipTone}`}
                    >
                      <Icon className="size-3.5" />
                    </span>
                    <span
                      className={`min-w-0 flex-1 break-words text-[11.5px] ${
                        notEvaluated
                          ? "text-muted-foreground"
                          : layersSettled || layersErrored
                            ? "text-foreground"
                            : "text-muted-foreground"
                      }`}
                    >
                      {layer.label}
                    </span>
                    <span
                      className={`flex-none rounded border px-1.5 py-0.5 text-[9.5px] ${pillTone}`}
                    >
                      {pillLabel}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Footer only carries the cross-cutting rule count. The raw
                status text (e.g. "Not Run") duplicates what the Cleared /
                Not cleared pills already say per layer, so we hide it. */}
            {sevenLayer && !!sevenLayer.crossCuttingRules && (
              <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border px-4 py-2 text-[11px] text-muted-foreground">
                <span>{sevenLayer.crossCuttingRules} cross-cutting rules</span>
              </div>
            )}
          </Panel>
        </div>
      </div>

      {footer && (
        <div className="relative z-10 flex flex-shrink-0 flex-wrap items-center justify-end gap-2 border-t border-border bg-card px-4 py-3 sm:px-5">
          {footer}
        </div>
      )}
    </div>
  );

  // Routed pages render in-flow; the validation flow shows the console as a
  // centred popup over a dimmed backdrop rather than taking the whole screen.
  if (inline) return body;

  // Overlay uses `overflow-y-auto` on the outermost layer, and the inner
  // flex row has `min-h-full` so the modal stays centred when it fits and
  // scrolls into view when the viewport is too short - lets the user reach
  // the full orbit visualization and seven-layer panel on shorter windows
  // (e.g. 1366×768 laptops) instead of the bottom being clipped.
  return (
    <div className="fixed inset-0 z-[70] overflow-y-auto p-3 sm:p-6">
      {/* Backdrop. Dismissable only once the run has finished, so a stray click
          cannot abort validation that is still in flight. */}
      <div
        className="fixed inset-0 bg-background/80 backdrop-blur-sm"
        onClick={run.done ? onCancel : undefined}
      />
      <div className="relative flex min-h-full items-center justify-center">
        {body}
      </div>
    </div>
  );
}
